import { use, useEffect } from 'react';
import { useStableCallback } from '@ifixit/ui/hooks/use-stable-callback';
import { GuideCompletionContext } from './GuideComplete';
import { GuideFavoriteContext } from './GuideFavorite';

/** Attach behavior to the PHP markup without rendering or replacing any elements. */
export function GuideStatsInteractions({ root }: { root: HTMLElement }) {
   const completion = use(GuideCompletionContext);
   const favorite = use(GuideFavoriteContext);
   useGuideStatAction(root, 'completion', completion.isCompleted, () => {
      if (completion.isCompleted) completion.removeComplete(App.guideid);
      else completion.complete(App.guideid);
   });
   useGuideStatAction(root, 'favorite', favorite.isFavorite, () => {
      if (favorite.isFavorite) favorite.unfavorite(App.guideid, App.langid, false);
      else favorite.favorite(App.guideid, App.langid, false);
   });
   useEffect(() => attachGuideStatPopovers(root), [root]);
   return null;
}

function useGuideStatAction(
   root: HTMLElement,
   kind: 'completion' | 'favorite',
   active: boolean,
   onClick: () => void
) {
   const handleClick = useStableCallback(onClick);
   useEffect(() => {
      const stat = root.querySelector<HTMLElement>(`[data-guide-stat="${kind}"]`);
      const button = stat?.querySelector<HTMLButtonElement>('[data-guide-stat-action]');
      if (!stat || !button) return;
      stat.querySelectorAll<HTMLElement>('[data-guide-stat-state]').forEach(element => {
         element.hidden = (element.dataset.guideStatState === 'active') !== active;
      });
      button.disabled = false;
      button.addEventListener('click', handleClick);
      return () => {
         button.removeEventListener('click', handleClick);
         button.disabled = true;
      };
   }, [root, kind, active, handleClick]);
}

function attachGuideStatPopovers(root: HTMLElement) {
   const controller = new AbortController();
   const { signal } = controller;
   const stats = [...root.querySelectorAll<HTMLDetailsElement>('[data-guide-stat]')];
   const cleanups = stats.map(stat => {
      const summary = stat.querySelector('summary');
      let closeTimer: ReturnType<typeof setTimeout> | undefined;
      let suppressFocus = false;
      const cancelClose = () => clearTimeout(closeTimer);
      const open = () => {
         cancelClose();
         stats.forEach(other => {
            if (other !== stat) other.open = false;
         });
         stat.open = true;
      };
      const enter = (event: PointerEvent) => {
         if (event.pointerType === 'mouse') open();
      };
      const leave = () => {
         cancelClose();
         closeTimer = setTimeout(() => {
            if (!stat.contains(document.activeElement)) stat.open = false;
         }, 150);
      };
      const pointerLeave = (event: PointerEvent) => {
         // A touch pointer leaves right after the tap that opened the menu.
         if (event.pointerType === 'mouse') leave();
      };
      const focus = () => {
         if (!suppressFocus) open();
      };
      const clickSummary = (event: MouseEvent) => {
         if (stat.open) event.preventDefault();
      };
      const outside = (event: PointerEvent) => {
         if (event.target instanceof Node && !stat.contains(event.target)) stat.open = false;
      };
      const escape = (event: KeyboardEvent) => {
         if (event.key !== 'Escape' || !stat.open) return;
         event.preventDefault();
         cancelClose();
         stat.open = false;
         suppressFocus = true;
         summary?.focus();
         suppressFocus = false;
      };
      stat.addEventListener('pointerenter', enter, { signal });
      stat.addEventListener('pointerleave', pointerLeave, { signal });
      stat.addEventListener('focusin', focus, { signal });
      stat.addEventListener('focusout', leave, { signal });
      // A menu opened by hover has no focus inside it, so Escape has to reach it from the document.
      const escapeAnywhere = (event: KeyboardEvent) => {
         if (event.key !== 'Escape' || !stat.open || stat.contains(document.activeElement)) return;
         cancelClose();
         stat.open = false;
      };
      stat.addEventListener('keydown', escape, { signal });
      document.addEventListener('keydown', escapeAnywhere, { signal });
      summary?.addEventListener('click', clickSummary, { signal });
      document.addEventListener('pointerdown', outside, { signal });
      return cancelClose;
   });
   const link = root.querySelector<HTMLAnchorElement>('[data-guide-comments]');
   const clearCommentsFocus = () => {
      document.querySelector('.js-page-comments')?.removeAttribute('tabindex');
   };
   const goToComments = (event: MouseEvent) => {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const comments = document.querySelector<HTMLElement>('.js-page-comments');
      if (!comments) return;
      event.preventDefault();
      stats.forEach(stat => {
         stat.open = false;
      });
      comments.tabIndex = -1;
      comments.addEventListener('blur', clearCommentsFocus, {
         signal,
         once: true,
      });
      comments.focus({ preventScroll: true });
      comments.scrollIntoView({ behavior: 'smooth' });
   };
   link?.addEventListener('click', goToComments, { signal });
   return () => {
      cleanups.forEach(cleanup => cleanup());
      controller.abort();
   };
}
